import React, { useState, useEffect } from 'react';

export default function Settings({ onClose }) {
  const [masterVolume, setMasterVolume] = useState(100);
  const [musicVolume, setMusicVolume] = useState(50);
  const [effectsVolume, setEffectsVolume] = useState(50);
  const [isMuted, setIsMuted] = useState(false);
  const [textSpeed, setTextSpeed] = useState(100);
  const [typeText, setTypeText] = useState(true);
  const [activeTab, setActiveTab] = useState('audio');

  useEffect(() => {
    setMasterVolume(window.gameAudio.savedVolumes.master * 100);
    setMusicVolume(window.gameAudio.savedVolumes.music * 100);
    setEffectsVolume(window.gameAudio.savedVolumes.effects * 100);
    setIsMuted(window.gameAudio.isMuted);
    setTextSpeed(window.gameLogic.getTypingSpeed());
    setTypeText(window.gameLogic.getToTypeText());
  }, []);

  const handleMasterVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setMasterVolume(value);
    window.gameAudio.setMasterVolume(value / 100);
  };

  const handleMusicVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setMusicVolume(value);
    window.gameAudio.setMusicVolume(value / 100);
  };

  const handleEffectsVolumeChange = (e) => {
    const value = parseInt(e.target.value);
    setEffectsVolume(value);
    window.gameAudio.setEffectsVolume(value / 100);
  };

  const handleMuteChange = (e) => {
    const muted = e.target.checked;
    setIsMuted(muted);
    window.gameAudio.setMute(muted);
  };

  const handleTextSpeedChange = (e) => {
    const value = parseInt(e.target.value);
    setTextSpeed(value);
    window.gameLogic.setTypingSpeed(value);
  };

  const handleTypeTextChange = (e) => {
    const value = e.target.checked;
    setTypeText(value);
    window.gameLogic.setToTypeText(value);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-lg pointer-events-none" />
      
      <div className="relative w-[800px] max-h-[80vh] bg-gradient-to-br from-[#1F1F2E]/95 to-[#2A3F5F]/95 rounded-3xl p-8 z-41
                    border border-[#FFD1DC]/20 shadow-[0_0_60px_rgba(255,209,220,0.15)]">
        
        {/* Decorative Elements */}
        <div className="absolute inset-0 pointer-events-none z-42 overflow-hidden rounded-3xl">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FFD1DC]/15 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#4A6FA5]/15 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FFD1DC]/5 to-transparent opacity-30" />
        </div>

        {/* Content */}
        <div className="relative z-43">
          <h2 className="text-3xl font-['Cormorant_Garamond'] font-semibold text-[#FFD1DC] mb-2">Settings</h2>
          <div className="w-32 h-0.5 bg-gradient-to-r from-[#FFD1DC]/50 to-[#4A6FA5]/50 mb-8" />

          <div className="flex space-x-4 mb-8">
            {['audio', 'game'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2 rounded-xl transition-all duration-300 font-['Montserrat'] text-base
                  ${activeTab === tab 
                    ? 'bg-gradient-to-r from-[#FFD1DC]/20 to-[#4A6FA5]/20 text-white shadow-[0_0_20px_rgba(255,209,220,0.15)]'
                    : 'text-white/50 hover:text-white/80'}`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="space-y-6 pr-4 max-h-[50vh] overflow-y-auto custom-scrollbar">
            {activeTab === 'audio' ? (
              <div className="space-y-6 animate-fade-in">
                {[
                  { label: 'Master Volume', value: masterVolume, onChange: handleMasterVolumeChange },
                  { label: 'Music Volume', value: musicVolume, onChange: handleMusicVolumeChange },
                  { label: 'Effects Volume', value: effectsVolume, onChange: handleEffectsVolumeChange }
                ].map((setting) => (
                  <div key={setting.label} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-white/90 font-['Montserrat'] text-sm">{setting.label}</span>
                      <span className="text-[#FFD1DC] font-['Montserrat'] text-sm">{setting.value}%</span>
                    </div>
                    <input
                      type="range"
                      value={setting.value}
                      onChange={setting.onChange}
                      className="w-full custom-slider"
                      min="0"
                      max="100"
                    />
                  </div>
                ))}
                
                <div className="flex items-center justify-between pt-2">
                  <span className="text-white/90 font-['Montserrat'] text-sm">Mute All</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isMuted}
                      onChange={handleMuteChange}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-[#1F1F2E] peer-focus:outline-none rounded-full
                                peer-checked:after:translate-x-full peer-checked:bg-[#FFD1DC]/30
                                after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                                after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all
                                after:shadow-[0_0_10px_rgba(255,209,220,0.3)]" />
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-white/90 font-['Montserrat'] text-sm">Text Speed</span>
                    <span className="text-[#FFD1DC] font-['Montserrat'] text-sm">{textSpeed}%</span>
                  </div>
                  <input
                    type="range"
                    value={textSpeed}
                    onChange={handleTextSpeedChange}
                    className="w-full custom-slider"
                    min="1"
                    max="200"
                  />
                </div>

                {[
                  { label: 'Type Text', checked: typeText, onChange: handleTypeTextChange },
                  { label: 'Auto-Save', checked: false, onChange: () => {} }
                ].map((setting) => (
                  <div key={setting.label} className="flex items-center justify-between pt-2">
                    <span className="text-white/90 font-['Montserrat'] text-sm">{setting.label}</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={setting.checked}
                        onChange={setting.onChange}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-[#1F1F2E] peer-focus:outline-none rounded-full
                                  peer-checked:after:translate-x-full peer-checked:bg-[#FFD1DC]/30
                                  after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                                  after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all
                                  after:shadow-[0_0_10px_rgba(255,209,220,0.3)]" />
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-44 text-white/50 hover:text-white transition-colors duration-300
                   w-8 h-8 rounded-full flex items-center justify-center
                   hover:bg-white/10"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 209, 220, 0.1);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 209, 220, 0.2);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 209, 220, 0.3);
        }
        .custom-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          background: rgba(255, 209, 220, 0.1);
          border-radius: 2px;
          outline: none;
        }
        .custom-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          background: linear-gradient(135deg, #FFD1DC, #FFB6C1);
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 0 10px rgba(255, 209, 220, 0.3);
        }
        .custom-slider::-webkit-slider-thumb:hover {
          transform: scale(1.1);
          box-shadow: 0 0 15px rgba(255, 209, 220, 0.4);
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
